/**
 * SPEC-AdminImpersonation §"Client wiring #3" — non-dismissable banner.
 *
 * Renders only when `useAuth().isImpersonating === true`. Top-of-viewport,
 * full-width, h-10. Yellow when read-only, red when writes are on.
 *
 *   Viewing as <Role> · <Actual Name> · <Read-only | Writes ON>
 *
 * Two buttons: `Toggle writes` and `Exit`. Both call the impersonation lib
 * which invalidates `/api/auth/user` + `/api/auth/check-approved` so the
 * banner color and the route gates update on the next render.
 *
 * The banner is mounted ABOVE the existing app shell (above
 * `AgentClaimSubscriptionProvider` / `SidebarProvider`) in `App.tsx` so
 * its h-10 push shifts every page consistently. See SPEC §"Client wiring
 * #4" — overlay collides with claim toasts, so we push the shell down
 * via a sibling layout block, not absolute positioning.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { stopImpersonation, toggleWrites } from "@/lib/impersonation";

const READ_ONLY_BG = "#FDE68A"; // amber-200 — SPEC §"Decisions locked #8": yellow when read-only
const READ_ONLY_TEXT = "#78350F"; // amber-900
const WRITES_ON_BG = "#FCA5A5"; // red-300 — red when writes are on
const WRITES_ON_TEXT = "#7F1D1D"; // red-900

function roleLabel(role: string | null | undefined): string {
  if (role === "agent") return "Agent";
  if (role === "senior") return "Senior";
  if (role === "admin") return "Admin";
  if (role === "bot") return "Bot";
  return role ?? "—";
}

export function ImpersonationBanner() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, isImpersonating, writesEnabled, effectiveRole } = useAuth();

  const toggleMutation = useMutation({
    mutationFn: (next: boolean) => toggleWrites(queryClient, next),
    onError: (e: any) => {
      toast({
        title: "Could not toggle writes",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    },
  });

  const exitMutation = useMutation({
    mutationFn: () => stopImpersonation(queryClient),
    onError: (e: any) => {
      toast({
        title: "Could not exit impersonation",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    },
  });

  if (!isImpersonating) return null;

  const bg = writesEnabled ? WRITES_ON_BG : READ_ONLY_BG;
  const fg = writesEnabled ? WRITES_ON_TEXT : READ_ONLY_TEXT;
  const stateLabel = writesEnabled ? "Writes ON" : "Read-only";

  return (
    <div
      data-testid="impersonation-banner"
      role="alert"
      aria-live="polite"
      className="w-full flex items-center justify-between gap-3 px-4"
      style={{
        height: 40,
        background: bg,
        color: fg,
        fontWeight: 600,
        borderBottom: `1px solid ${fg}`,
      }}
    >
      <span
        data-testid="impersonation-banner-text"
        style={{ fontSize: 14, letterSpacing: "-0.005em" }}
      >
        Viewing as {roleLabel(effectiveRole)} · {user?.name ?? user?.email ?? "—"} · {stateLabel}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="impersonation-banner-toggle-writes"
          disabled={toggleMutation.isPending}
          onClick={() => toggleMutation.mutate(!writesEnabled)}
          style={{
            height: 28,
            padding: "0 12px",
            borderRadius: 6,
            border: `1px solid ${fg}`,
            background: "transparent",
            color: fg,
            fontSize: 13,
            fontWeight: 600,
            cursor: toggleMutation.isPending ? "wait" : "pointer",
          }}
        >
          {writesEnabled ? "Disable writes" : "Toggle writes"}
        </button>
        <button
          type="button"
          data-testid="impersonation-banner-exit"
          disabled={exitMutation.isPending}
          onClick={() => exitMutation.mutate()}
          style={{
            height: 28,
            padding: "0 12px",
            borderRadius: 6,
            border: `1px solid ${fg}`,
            background: fg,
            color: bg,
            fontSize: 13,
            fontWeight: 600,
            cursor: exitMutation.isPending ? "wait" : "pointer",
          }}
        >
          Exit
        </button>
      </div>
    </div>
  );
}

export default ImpersonationBanner;
